import { 
  MapPin,
  CreditCard,
  Bell,
  Zap,
  QrCode,
  Navigation,
  Star,
  Play,
  Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import { AppleLogo } from "@/components/icons/AppleLogo";
import mobileApp1 from "@/assets/mobile_app.png";
import mobileApp2 from "@/assets/mobile_app_2.png";
import mobileAppScreens from "@/assets/mobile_app_screens.jpg";

const appFeatures = [
  { icon: MapPin, title: "Find Stations", description: "Locate nearby chargers with real-time availability" },
  { icon: QrCode, title: "Scan & Charge", description: "Start sessions with a simple QR scan" },
  { icon: CreditCard, title: "Easy Payment", description: "Apple Pay, Google Pay, cards, and wallets" },
  { icon: Bell, title: "Notifications", description: "Alerts when charging completes or issues arise" },
  { icon: Navigation, title: "Navigation", description: "Integrated directions to any station" },
  { icon: Star, title: "Favorites", description: "Save preferred stations for quick access" },
];

export function MobileAppsNew() {
  return (
    <section id="apps" className="py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute top-0 left-0 w-full section-divider" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <ScrollReveal>
            <motion.div 
              className="inline-flex items-center gap-2 badge-primary mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Mobile Experience
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Mobile <span className="text-gradient">Apps</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-muted-foreground lg:max-w-md text-lg font-light leading-relaxed">
              White-label iOS and Android apps that put the complete charging experience in drivers' hands.
            </p>
          </ScrollReveal>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Features */}
          <div className="lg:col-span-4">
            <StaggerContainer className="grid grid-cols-2 gap-4" staggerDelay={0.08}>
              {appFeatures.map((feature, idx) => (
                <StaggerItem key={idx}>
                  <motion.div
                    whileHover={{ scale: 1.03, y: -4 }}
                    transition={{ duration: 0.3 }}
                    className="glass-card p-6 hover:border-primary/40 transition-all group"
                  >
                    <motion.div 
                      className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <feature.icon className="h-5 w-5 text-primary" />
                    </motion.div>
                    <h3 className="font-semibold mb-1 text-white">{feature.title}</h3>
                    <p className="text-sm text-white/60 font-light">{feature.description}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>

          {/* Phone Mockups */}
          <ScrollReveal className="lg:col-span-8">
            <div className="flex justify-center items-center">
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={mobileApp1} 
                  alt="EVGateway mobile app showing charging session and station map" 
                  className="w-full max-w-lg object-contain drop-shadow-2xl"
                />
              </motion.div>
            </div>
          </ScrollReveal>
        </div>

        {/* App Screens Showcase */}
        <ScrollReveal className="mt-24">
          <motion.div 
            className="glass-card p-6 md:p-10 rounded-3xl"
            whileHover={{ scale: 1.003 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center tracking-tight text-white">Complete Driver Experience</h3>
            <img 
              src={mobileAppScreens} 
              alt="EVGateway mobile app screens showing sign in, station finding, filters, favorites, payments, and charging session" 
              className="w-full object-contain rounded-2xl"
            />
          </motion.div>
        </ScrollReveal>

        {/* Secondary Image */}
        <ScrollReveal className="mt-16">
          <div className="flex justify-center">
            <motion.img 
              src={mobileApp2} 
              alt="EVGateway mobile app additional screens" 
              className="w-full max-w-2xl object-contain drop-shadow-xl"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </ScrollReveal>

        {/* App Store Badges */}
        <ScrollReveal className="mt-20">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <motion.a 
              href="https://apps.apple.com/us/app/evgateway/id1202982043" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-3 glass-card px-6 py-4 hover:border-primary/40 transition-all"
            >
              <AppleLogo className="w-8 h-8 text-white" />
              <div>
                <div className="text-[10px] text-white/60">Download on the</div>
                <div className="text-sm font-semibold text-white">App Store</div>
              </div>
            </motion.a>
            <motion.a 
              href="https://play.google.com/store/apps/details?id=com.EVGATEWAY&hl=en_US" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-3 glass-card px-6 py-4 hover:border-primary/40 transition-all"
            >
              <Play className="w-8 h-8 text-white fill-white" />
              <div>
                <div className="text-[10px] text-white/60">Get it on</div>
                <div className="text-sm font-semibold text-white">Google Play</div>
              </div>
            </motion.a>
          </div>
        </ScrollReveal>
      </div>
      
      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}